import { useState, useRef, useCallback, useEffect } from "react";
import { Hero } from "./Hero";
import { Analyzer } from "./Analyzer";
import { VerdictCard } from "./VerdictCard";
import { Footer } from "./Footer";
import { AccessBadge } from "./AccessBadge";
import { PaywallModal } from "./PaywallModal";
import { RestoreAccessModal } from "./RestoreAccessModal";
import { SupportingSections } from "./SupportingSections";
import { FounderStoryModal } from "./FounderStoryModal";
import { ClosingCTA } from "./ClosingCTA";
import { SAMPLE_REDFLAG, SAMPLE_HEALTHY, buildFull } from "../lib/samples";
import { authedAxios, useAccess } from "../lib/useAccess";
import { trackEvent, usePageview } from "../lib/useAnalytics";
import { Loader2 } from "lucide-react";

export const ScoundrelScout = () => {
  const [mode, setMode] = useState("full");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [platform, setPlatform] = useState("");

  usePageview();

  const { access, refresh } = useAccess();
  const [products, setProducts] = useState([]);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [founderOpen, setFounderOpen] = useState(false);
  const [checkoutBanner, setCheckoutBanner] = useState(null); // "polling" | "success" | "cancelled"
  const [portalLoading, setPortalLoading] = useState(false);

  const analyzerRef = useRef(null);
  const verdictRef = useRef(null);

  const scrollTo = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (result) {
      requestAnimationFrame(() => {
        if (verdictRef.current) {
          verdictRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
  }, [result]);

  // Load product catalog once.
  useEffect(() => {
    let cancelled = false;
    authedAxios()
      .get("/products")
      .then(({ data }) => {
        if (!cancelled) setProducts(data?.products || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle redirect back from Stripe (?checkout=success&session_id=... or ?checkout=cancelled)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");
    if (!checkout) return;

    // Clean the URL.
    window.history.replaceState({}, "", window.location.pathname);

    if (checkout === "cancelled") {
      trackEvent("checkout_returned_cancelled");
      setCheckoutBanner({ kind: "cancelled" });
      return;
    }
    if (checkout === "success") {
      trackEvent("checkout_returned_success");
      setCheckoutBanner({ kind: "polling" });
      // Poll status briefly while the webhook completes the entitlement.
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          if (sessionId) {
            await authedAxios().get(`/checkout/status/${sessionId}`);
          }
          await refresh();
        } catch {
          /* ignore */
        }
        if (attempts < 8) {
          setTimeout(poll, 1500);
        } else {
          setCheckoutBanner({ kind: "success" });
        }
      };
      poll();
    }
  }, [refresh]);

  // When the access status transitions from unknown to paid AFTER a checkout
  // success, lock in the success banner.
  useEffect(() => {
    if (
      checkoutBanner?.kind === "polling" &&
      access &&
      access.tier !== "free" &&
      (access.reads_remaining ?? 0) > 0
    ) {
      setCheckoutBanner({ kind: "success" });
    }
  }, [access, checkoutBanner]);

  const goToAnalyzer = (nextMode) => {
    setMode(nextMode);
    setResult(null);
    setError("");
    requestAnimationFrame(() => scrollTo(analyzerRef));
  };

  // Single "Read the Subtext" CTA from hero + closing band lands in the
  // free demo flow (default mode = "full").
  const onReadSubtext = () => {
    trackEvent("hero_cta_clicked");
    goToAnalyzer("full");
  };

  const fillSample = (sample) => {
    setMode("full");
    setText(buildFull(sample));
    setResult(null);
    setError("");
    requestAnimationFrame(() => scrollTo(analyzerRef));
  };

  const submit = useCallback(async () => {
    setError("");
    if (!text.trim()) {
      setError("Need something to read. Paste a profile or chat first.");
      return;
    }
    if (mode === "matched" && !platform) {
      setError("Pick where you matched first, then we can read it.");
      return;
    }
    trackEvent("analyze_started", { tier: mode, platform_source: mode === "matched" ? platform : null });
    setLoading(true);
    setResult(null);
    try {
      const body = { mode, text };
      if (mode === "matched" && platform) body.platform = platform;
      const { data } = await authedAxios().post("/analyze", body);
      if (data && data.ok) {
        setResult(data);
        // A paid read decrements server-side — keep the badge in sync.
        if (!data.locked) refresh();
      } else {
        setError("Couldn't get a clean read on that. Try again.");
      }
    } catch (err) {
      setError("Couldn't reach the Wingman. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }, [mode, text, refresh, platform]);

  const reset = () => {
    setResult(null);
    setError("");
    setText("");
    requestAnimationFrame(() => scrollTo(analyzerRef));
  };

  const openPaywall = () => {
    trackEvent("paywall_opened");
    setPaywallOpen(true);
  };
  const openRestore = () => {
    trackEvent("restore_opened");
    setRestoreOpen(true);
  };
  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await authedAxios().post("/access/portal", {
        return_url: window.location.origin,
      });
      if (data?.url) window.location.assign(data.url);
    } catch {
      setError("Couldn’t open billing portal. Try again in a moment.");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div data-testid="ss-app" className="App">
      {/* Sticky top access bar */}
      <div
        data-testid="access-bar"
        className="sticky top-0 z-30 px-5 sm:px-8 py-2.5 border-b flex justify-end"
        style={{
          background: "rgba(250, 246, 238, 0.92)",
          backdropFilter: "blur(8px)",
          borderColor: "var(--ss-rule-line)",
        }}
      >
        <AccessBadge
          access={access}
          onOpenPaywall={openPaywall}
          onOpenRestore={openRestore}
          onOpenPortal={openPortal}
        />
      </div>

      {checkoutBanner && (
        <CheckoutBanner
          banner={checkoutBanner}
          portalLoading={portalLoading}
          onDismiss={() => setCheckoutBanner(null)}
          onOpenPaywall={openPaywall}
        />
      )}

      <Hero
        onReadSubtext={onReadSubtext}
        onSampleRedFlag={() => fillSample(SAMPLE_REDFLAG)}
        onSampleHealthy={() => fillSample(SAMPLE_HEALTHY)}
      />

      <SupportingSections onOpenFounder={() => setFounderOpen(true)} />

      <Analyzer
        ref={analyzerRef}
        value={text}
        mode={mode}
        onChange={setText}
        onModeChange={setMode}
        onSubmit={submit}
        loading={loading}
        error={error}
        platform={platform}
        onPlatformChange={setPlatform}
      />

      <div ref={verdictRef}>
        {result && (
          <VerdictCard
            result={result}
            onReset={reset}
            onUnlock={openPaywall}
          />
        )}
      </div>

      <ClosingCTA onReadSubtext={onReadSubtext} />

      <Footer />

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        products={products}
      />

      <RestoreAccessModal
        open={restoreOpen}
        onClose={() => setRestoreOpen(false)}
        onRestored={refresh}
      />

      <FounderStoryModal
        open={founderOpen}
        onClose={() => setFounderOpen(false)}
      />
    </div>
  );
};

const CheckoutBanner = ({ banner, portalLoading, onDismiss, onOpenPaywall }) => {
  if (banner.kind === "polling") {
    return (
      <div
        data-testid="checkout-banner-polling"
        className="px-5 sm:px-8 py-3 flex items-center gap-3"
        style={{ background: "var(--ss-amber-bg)", borderBottom: "1px solid var(--ss-amber-rail)" }}
      >
        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.25} style={{ color: "var(--ss-amber)" }} />
        <p className="text-sm" style={{ color: "var(--ss-ink)" }}>
          Finishing up your purchase{portalLoading ? "…" : "…"}
        </p>
      </div>
    );
  }
  if (banner.kind === "success") {
    return (
      <div
        data-testid="checkout-banner-success"
        className="px-5 sm:px-8 py-3 flex items-center gap-3"
        style={{ background: "var(--ss-green-bg)", borderBottom: "1px solid var(--ss-green-rail)" }}
      >
        <p className="text-sm flex-1" style={{ color: "var(--ss-ink)" }}>
          <strong>You’re in.</strong> Your full reads are ready — paste a
          profile or chat to get the full read.
        </p>
        <button onClick={onDismiss} className="ss-ghost-sm" data-testid="checkout-banner-dismiss">
          Got it
        </button>
      </div>
    );
  }
  if (banner.kind === "cancelled") {
    return (
      <div
        data-testid="checkout-banner-cancelled"
        className="px-5 sm:px-8 py-3 flex items-center gap-3"
        style={{ background: "var(--ss-red-bg)", borderBottom: "1px solid var(--ss-red-rail)" }}
      >
        <p className="text-sm flex-1" style={{ color: "var(--ss-ink)" }}>
          Checkout cancelled. No charge made — your free preview is still active.
        </p>
        <button onClick={onOpenPaywall} className="ss-ghost-sm" data-testid="checkout-banner-retry">
          See plans again
        </button>
        <button onClick={onDismiss} className="ss-ghost-sm" data-testid="checkout-banner-dismiss">
          Dismiss
        </button>
      </div>
    );
  }
  return null;
};
